import React, { useState, useEffect, useRef } from "react";
import { View, Pressable } from "react-native-web";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomText from "../../components/atoms/CustomText";
import { colors, spacing, typography } from "../../utils/styling";
import { useResponsive } from "../../hooks/useResponsive";
import { INFO_COLLECTION_QUESTIONS } from "../../utils/infoCollectionQuestions";
// No profile yet in this flow; update quiz info instead
import { useUpdateQuizWithInfo } from "../../lib/quizzes";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PersonalityTestPDF from "../../components/common/PersonalityTestPDF";
import { getResultsDataFromStateOrStorage } from "../../utils/resultsData";

const NUM_SECTIONS = INFO_COLLECTION_QUESTIONS.length;

interface ResultsData {
  // The three sub-personality fields below are OPTIONAL as of 2026-08-20
  // (ClickUp 86bb0f9vz). New results never carry them — the sub-quiz is gone —
  // but results stored in localStorage before that date still do, and
  // `specificDominantCategory` is the key that resolves a pre-existing user's
  // workbook exercises. Do not delete them: that would strip the field on the
  // way through and silently empty an existing user's saved workbook.
  // in case localStorage is not working
  answers: number[];
  specificAnswers?: number[];
  categoryScores: Record<string, number>;
  specificCategoryScores?: Record<string, number>;
  dominantCategory: string;
  specificDominantCategory?: string;
  sfAccount?: string | null;
  quizId?: number;
  leadId?: number;
  email?: string;
}

const InfoCollectionQuestion: React.FC<{
  question: string;
  options: string[];
  value: string | undefined;
  onChange: (value: string) => void;
  onNext: () => void;
}> = ({ question, options, value, onChange, onNext }) => {
  const { isMobile } = useResponsive();

  const handleSelectOption = (option: string) => {
    onChange(option);
  };

  // Calculate layout for 2 columns
  const rows = Math.ceil(options.length / 2);

  return (
    <View
      style={{
        backgroundColor: colors.white,
        borderRadius: 16,
        boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
        padding: isMobile ? spacing.md : spacing.xl,
        maxWidth: 900,
        width: "100%",
        marginBottom: spacing.xxl,
      }}
    >
      {/* Question */}
      <CustomText
        style={{
          fontSize: isMobile ? 24 : 36,
          fontWeight: "600",
          color: colors.dark,
          fontFamily: "Nunito",
          textAlign: "center",
          marginBottom: spacing.xl,
        }}
      >
        {question}
      </CustomText>

      {/* Options in 2 columns */}
      <View
        style={{
          display: "flex",
          flexDirection: "column",
          gap: spacing.md,
          width: "100%",
        }}
      >
        {Array.from({ length: rows }, (_, rowIndex) => (
          <View
            key={rowIndex}
            style={{
              display: "flex",
              flexDirection: "row",
              gap: spacing.md,
              width: "100%",
            }}
          >
            {/* First column option */}
            {options[rowIndex * 2] && (
              <Pressable
                onPress={() => handleSelectOption(options[rowIndex * 2])}
                style={{
                  backgroundColor:
                    value === options[rowIndex * 2]
                      ? colors.gray[300]
                      : colors.gray[100],
                  borderRadius: 12,
                  padding: spacing.lg,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderWidth: 1,
                  borderColor:
                    value === options[rowIndex * 2]
                      ? colors.gray[700]
                      : colors.gray[100],
                  transition: "all 0.2s",
                  cursor: "pointer",
                  minHeight: isMobile ? 60 : 80,
                }}
              >
                <CustomText
                  style={{
                    fontSize: isMobile ? 16 : 20,
                    fontWeight: "500",
                    fontFamily: "Nunito",
                    color: colors.dark,
                    textAlign: "center",
                  }}
                >
                  {options[rowIndex * 2]}
                </CustomText>
              </Pressable>
            )}

            {/* Second column option */}
            {options[rowIndex * 2 + 1] && (
              <Pressable
                onPress={() => handleSelectOption(options[rowIndex * 2 + 1])}
                style={{
                  backgroundColor:
                    value === options[rowIndex * 2 + 1]
                      ? colors.gray[300]
                      : colors.gray[100],
                  borderRadius: 12,
                  padding: spacing.lg,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderWidth: 1,
                  borderColor:
                    value === options[rowIndex * 2 + 1]
                      ? colors.gray[700]
                      : colors.gray[100],
                  transition: "all 0.2s",
                  cursor: "pointer",
                  minHeight: isMobile ? 60 : 80,
                }}
              >
                <CustomText
                  style={{
                    fontSize: isMobile ? 16 : 20,
                    fontWeight: "500",
                    fontFamily: "Nunito",
                    color: colors.dark,
                    textAlign: "center",
                  }}
                >
                  {options[rowIndex * 2 + 1]}
                </CustomText>
              </Pressable>
            )}
          </View>
        ))}
      </View>
    </View>
  );
};

const SectionedInfoCollection: React.FC<{
  onComplete: (answers: Record<string, string>) => void;
  onAnswerUpdate: (answers: Record<string, string>) => void;
  onSectionUpdate: (section: number) => void;
}> = ({ onComplete, onAnswerUpdate, onSectionUpdate }) => {
  const { isMobile } = useResponsive();
  const [section, setSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const currentQuestion = INFO_COLLECTION_QUESTIONS[section];
  const currentAnswer = answers[currentQuestion.question];

  const handleAnswer = (value: string) => {
    const newAnswers = {
      ...answers,
      [currentQuestion.question]: value,
    };
    setAnswers(newAnswers);
    onAnswerUpdate(newAnswers);
  };

  const handleNext = () => {
    if (section < NUM_SECTIONS - 1) {
      setSection((s) => s + 1);
    } else {
      // All questions answered, complete the form
      onComplete(answers);
    }
  };

  const handleBack = () => {
    if (section > 0) {
      setSection((s) => s - 1);
    }
  };

  // Scroll to top when section changes
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [containerRef, section]);
  console.log("section", section);

  return (
    <View
      ref={containerRef}
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: isMobile ? spacing.md : spacing.xl,
        paddingLeft: isMobile ? spacing.lg : spacing.xxl,
        paddingRight: isMobile ? spacing.lg : spacing.xxl,
        marginBottom: spacing.xxl,
        scrollMarginTop: 150,
      }}
    >
      {/* Instructions card - only show on first section */}
      {section === 0 && (
        <View
          style={{
            backgroundColor: colors.white,
            borderRadius: 16,
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
            padding: isMobile ? spacing.md : spacing.xl,
            maxWidth: 900,
            width: "100%",
            marginBottom: spacing.xxl,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <CustomText
              style={{
                fontWeight: 700,
                fontSize: isMobile ? 24 : 36,
                fontFamily: "'Nunito', sans-serif",
              }}
            >
              📋 Information Collection
            </CustomText>
          </View>

          <View
            style={{
              display: "flex",
              flexDirection: "column",
              flexWrap: "wrap",
              alignItems: "flex-start",
              marginBottom: 12,
            }}
          >
            <CustomText
              style={{
                fontSize: isMobile ? 16 : 24,
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 400,
                wordBreak: "break-word",
                whiteSpace: "normal",
              }}
            >
              Please answer the following questions to help your AI assistant
              understand your financial situation.
            </CustomText>
          </View>

          <CustomText
            style={{
              fontSize: isMobile ? 16 : 24,
              color: "#444",
              marginBottom: 28,
              fontFamily: "'Nunito', sans-serif",
            }}
          >
            Est. time to complete: 1 minute
          </CustomText>
        </View>
      )}

      {/* Current question */}
      <InfoCollectionQuestion
        question={currentQuestion.question}
        options={currentQuestion.options}
        value={currentAnswer}
        onChange={handleAnswer}
        onNext={handleNext}
      />

      {!isMobile && (
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
            marginTop: spacing.xl,
            gap: spacing.lg,
          }}
        >
          {section > 0 && (
            <Pressable
              style={{
                backgroundColor: colors.gray[200],
                paddingVertical: isMobile ? spacing.sm : spacing.md,
                paddingHorizontal: isMobile ? spacing.lg : spacing.xl,
                borderRadius: 12,
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onPress={handleBack}
            >
              <CustomText
                style={{
                  color: colors.dark,
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: "600",
                  fontFamily: "Nunito",
                }}
              >
                Back
              </CustomText>
            </Pressable>
          )}

          <Pressable
            style={{
              backgroundColor: currentAnswer ? "#748469" : colors.gray[300],
              paddingVertical: isMobile ? spacing.sm : spacing.md,
              paddingHorizontal: isMobile ? spacing.lg : spacing.xl,
              borderRadius: 12,
              cursor: currentAnswer ? "pointer" : "not-allowed",
              transition: "all 0.2s",
            }}
            disabled={!currentAnswer}
            onPress={handleNext}
          >
            <CustomText
              style={{
                color: colors.white,
                fontSize: isMobile ? 16 : 18,
                fontWeight: "600",
                fontFamily: "Nunito",
              }}
            >
              {section < NUM_SECTIONS - 1 ? "Next" : "Submit"}
            </CustomText>
          </Pressable>
        </View>
      )}
    </View>
  );
};

export default function InfoCollection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId } = useParams();
  const { isMobile } = useResponsive();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [currentAnswers, setCurrentAnswers] = useState<Record<string, string>>(
    {}
  );
  const [currentSection, setCurrentSection] = useState<number>(0);

  const resultsData = getResultsDataFromStateOrStorage(
    location.state
  ) as ResultsData;
  const { sfAccount } = resultsData || {};

  useEffect(() => {
    console.log("Info Collection page loaded with clientId:", clientId);
  }, [clientId]);

  const { mutateAsync: updateQuizWithInfo } = useUpdateQuizWithInfo();

  // on submission, update quiz with user info (no profile yet)
  const handleComplete = async (answers: Record<string, string>) => {
    setIsSubmitting(true);
    console.log("User information collected:", answers);

    try {
      // Transform answers to a simple key-value object for profile info
      const profileInfo = Object.entries(answers).reduce(
        (acc, [question, answer]) => {
          acc[question] = answer;
          return acc;
        },
        {} as Record<string, string>
      );

      console.log("sending personalInfo to updateQuizWithInfo", profileInfo);
      if (resultsData?.quizId) {
        await updateQuizWithInfo({
          quizId: resultsData.quizId,
          info: profileInfo,
        });
      }

      // Store personal info collection answers in localStorage
      localStorage.setItem("mpay_personalInfo", JSON.stringify(currentAnswers));
      console.log("Personal info stored in localStorage:", currentAnswers);
    } catch (error: any) {
      console.error("Failed to submit user information:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
      });
    } finally {
      setIsSubmitting(false);
      // After saving info to quiz, continue to ContactSubmit if not sf client
      if (sfAccount) {
        navigate("/quiz/results-ready", {
          state: { ...resultsData, from: location.pathname },
        });
      } else {
        navigate("/quiz/results/submit", {
          state: { ...resultsData, from: location.pathname },
        });
      }
    }
  };

  const handleAnswerUpdate = (answers: Record<string, string>) => {
    setCurrentAnswers(answers);
  };

  const ProgressFooter = () => {
    const answeredCount = Object.keys(currentAnswers).length;
    const progress = answeredCount / INFO_COLLECTION_QUESTIONS.length;
    const canGoBack = currentSection > 0;

    return (
      <View
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: colors.white,
          borderTop: `1px solid ${colors.gray[200]}`,
          padding: isMobile ? spacing.sm : spacing.md,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: isMobile ? "space-around" : "center",
          gap: isMobile ? spacing.xs : spacing.md,
          zIndex: 100,
          boxShadow: "0 -1px 6px rgba(0,0,0,0.04)",
        }}
      >
        {isMobile ? (
          <>
            {/* Back Button */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                width: 80,
                justifyContent: "center",
              }}
            >
              <Pressable
                style={{
                  backgroundColor: colors.gray[200],
                  paddingVertical: spacing.xs,
                  paddingHorizontal: spacing.sm,
                  borderRadius: 8,
                  cursor: canGoBack ? "pointer" : "default",
                  width: "100%",
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  visibility: canGoBack ? "visible" : "hidden",
                }}
                onPress={() => {
                  if (currentSection > 0) {
                    setCurrentSection(currentSection - 1);
                  }
                }}
                disabled={!canGoBack}
              >
                <CustomText
                  style={{
                    color: colors.dark,
                    fontSize: 14,
                    fontWeight: "600",
                    fontFamily: "Nunito",
                  }}
                >
                  Back
                </CustomText>
              </Pressable>
            </View>
            {/* Progress section */}
            <View
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <View
                style={{
                  width: isMobile ? 150 : 200,
                  height: 8,
                  backgroundColor: colors.gray[200],
                  borderRadius: 4,
                  overflow: "hidden",
                  flexShrink: 0,
                  marginHorizontal: isMobile ? 10 : 0,
                }}
              >
                <View
                  style={{
                    width: `${progress * 100}%`,
                    height: "100%",
                    backgroundColor: colors.primary,
                    transition: "width 0.3s ease-in-out",
                  }}
                />
              </View>
              <CustomText
                style={{
                  color: colors.gray[700],
                  fontSize: isMobile ? 13 : 16,
                  fontFamily: "Nunito",
                  whiteSpace: "nowrap",
                }}
              >
                {Math.round(progress * 100)}%
              </CustomText>
            </View>
            {/* Next Button */}
            <View
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                width: 80,
              }}
            >
              <Pressable
                style={{
                  width: "100%",
                  height: 32,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor:
                    answeredCount > 0 ? "#748469" : colors.gray[300],
                  paddingVertical: spacing.xs,
                  paddingHorizontal: spacing.sm,
                  borderRadius: 8,
                  cursor: answeredCount > 0 ? "pointer" : "not-allowed",
                  opacity: answeredCount > 0 ? 1 : 0.5,
                }}
                onPress={() => {
                  if (answeredCount === 12) handleComplete(currentAnswers);
                }}
                disabled={answeredCount === 0}
              >
                <CustomText
                  style={{
                    color: colors.white,
                    fontSize: 14,
                    fontWeight: "600",
                    fontFamily: "Nunito",
                  }}
                >
                  {answeredCount === 11 ? "Submit" : "Next"}
                </CustomText>
              </Pressable>
            </View>
          </>
        ) : (
          <>
            <CustomText
              style={{
                color: colors.gray[700],
                fontSize: isMobile ? 14 : 16,
                fontFamily: "Nunito",
                whiteSpace: "nowrap",
              }}
            >
              {answeredCount} of {INFO_COLLECTION_QUESTIONS.length} answered
            </CustomText>
            <View
              style={{
                width: isMobile ? 150 : 200,
                height: 8,
                backgroundColor: colors.gray[200],
                borderRadius: 4,
                overflow: "hidden",
                flexShrink: 0,
                marginHorizontal: isMobile ? 10 : 0,
              }}
            >
              <View
                style={{
                  width: `${progress * 100}%`,
                  height: "100%",
                  backgroundColor: colors.primary,
                  transition: "width 0.3s ease-in-out",
                }}
              />
            </View>
            <CustomText
              style={{
                color: colors.gray[700],
                fontSize: isMobile ? 13 : 16,
                fontFamily: "Nunito",
                whiteSpace: "nowrap",
              }}
            >
              {Math.round(progress * 100)}%
            </CustomText>
          </>
        )}
      </View>
    );
  };

  return (
    <View style={{ flex: 1, position: "relative", minHeight: "100vh" }}>
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          padding: isMobile ? spacing.sm : spacing.xl,
          paddingBottom: isMobile ? spacing.xxl : spacing.xl,
        }}
      >
        <SectionedInfoCollection
          onComplete={handleComplete}
          onAnswerUpdate={handleAnswerUpdate}
          onSectionUpdate={setCurrentSection}
        />
      </View>

      {isSubmitting && (
        <View
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: colors.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CustomText
            style={{
              fontSize: isMobile ? 24 : 36,
              fontWeight: "600",
              color: colors.dark,
              fontFamily: "Nunito",
              textAlign: "center",
            }}
          >
            Submitting your information...
          </CustomText>
        </View>
      )}

      <ProgressFooter />
    </View>
  );
}
